import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShoppingCart, Heart, Minus, Plus, Check, AlertTriangle, ChevronLeft, Lock } from 'lucide-react';
import { catalogAPI, wishlistAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import AuthGate from '../AuthGate';
import ProductCard from '../ProductCard';
import Breadcrumbs from '../layout/Breadcrumbs';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

export default function ProductPage() {
  return (
    <AuthGate>
      <ProductPageContent />
    </AuthGate>
  );
}

function ProductPageContent() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const loadProduct = useCallback(async () => {
    setLoading(true);
    try {
      const res = await catalogAPI.getProduct(slug);
      setProduct(res.data.product);
      setRelated(res.data.product.related_products || []);
      setSelectedImage(0);
      setQuantity(1);
    } catch {
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const getCurrentPrice = () => {
    if (!product?.tier_pricing?.length) return 0;
    const sorted = [...product.tier_pricing].sort((a, b) => a.min_qty - b.min_qty);
    for (const tier of sorted) {
      if (tier.max_qty === null || quantity <= tier.max_qty) {
        if (quantity >= tier.min_qty) return tier.price;
      }
    }
    return sorted[sorted.length - 1].price;
  };

  const handleAddToCart = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      await addToCart(product._id, quantity);
      toast.success(`${product.name} added to cart!`);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to add to cart'));
    }
  };

  const handleWishlist = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      await wishlistAPI.addToWishlist(product._id);
      toast.success('Added to wishlist!');
    } catch (err) {
      toast.error('Failed to add to wishlist');
    }
  };

  if (loading) return (
    <div className="pw-container py-8">
      <div className="animate-pulse grid md:grid-cols-2 gap-8">
        <div className="aspect-square bg-[#fbfaf8] rounded-xl" />
        <div className="space-y-4">
          <div className="h-6 bg-[#fbfaf8] rounded w-3/4" />
          <div className="h-4 bg-[#fbfaf8] rounded w-1/2" />
          <div className="h-32 bg-[#fbfaf8] rounded" />
        </div>
      </div>
    </div>
  );

  if (!product) return null;

  const currentPrice = getCurrentPrice();
  const lineTotal = (currentPrice * quantity).toFixed(2);
  const images = product.images?.length > 0 ? product.images : ['https://images.unsplash.com/photo-1490750967868-88aa4f44baee?w=600'];
  const stockStatus = product.stock <= 0 ? 'out_of_stock' : product.stock <= (product.low_stock_threshold || 10) ? 'low_stock' : 'in_stock';

  return (
    <div className="pb-20 md:pb-8">
      <div className="pw-container py-2">
        <Breadcrumbs items={[
          { label: 'Our Varieties', href: '/shop' },
          { label: product.category, href: `/shop?category=${encodeURIComponent(product.category)}` },
          { label: product.name }
        ]} />

        <div className="grid md:grid-cols-2 gap-6 md:gap-10">
          {/* Gallery */}
          <div>
            <div className="rounded-xl overflow-hidden bg-[#fbfaf8] mb-3">
              <img src={images[selectedImage]} alt={product.name} className="w-full h-auto object-contain" />
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)} className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 ${selectedImage === i ? 'border-[#2f6b4f]' : 'border-transparent'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="text-xl md:text-2xl font-semibold mb-1">{product.name}</h1>
            <p className="text-xs text-[#6f6a63] mb-3">SKU: {product.sku}</p>

            {/* Stock Status */}
            <div data-testid="product-stock-indicator" className="mb-4">
              {stockStatus === 'in_stock' && (
                <span className="flex items-center gap-1 text-sm text-green-700"><Check size={14} /> In Stock ({product.stock} available)</span>
              )}
              {stockStatus === 'low_stock' && (
                <span className="flex items-center gap-1 text-sm text-amber-600"><AlertTriangle size={14} /> Low Stock ({product.stock} remaining)</span>
              )}
              {stockStatus === 'out_of_stock' && (
                <span className="flex items-center gap-1 text-sm text-red-600"><AlertTriangle size={14} /> Out of Stock</span>
              )}
            </div>

            {/* Tier Pricing Table - only for authenticated users */}
            {user && product.tier_pricing?.length > 0 && (
              <div data-testid="product-tier-pricing-table" className="mb-6">
                <h3 className="text-xs tracking-wider uppercase text-[#6f6a63] mb-2">Volume Pricing</h3>
                <div className="border border-[#e7e3dd] rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#fbfaf8]">
                      <tr>
                        <th className="text-left px-4 py-2 text-xs tracking-wider uppercase text-[#6f6a63]">Quantity</th>
                        <th className="text-right px-4 py-2 text-xs tracking-wider uppercase text-[#6f6a63]">Price per unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.tier_pricing.map((tier, i) => {
                        const isActive = quantity >= tier.min_qty && (tier.max_qty === null || quantity <= tier.max_qty);
                        return (
                          <tr key={i} className={isActive ? 'bg-[#e7f1ea]' : ''}>
                            <td className="px-4 py-2">
                              {tier.min_qty}{tier.max_qty ? `-${tier.max_qty}` : '+'} units
                            </td>
                            <td className="text-right px-4 py-2 font-semibold">${tier.price.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Guest CTA - Log in to see prices */}
            {!user && (
              <div className="mb-6 bg-[#fbfaf8] border border-[#e7e3dd] rounded-xl p-6 text-center">
                <Lock size={24} className="mx-auto text-[#6f6a63] mb-3" />
                <p className="text-sm font-medium text-[#2d2d2d] mb-1">Log in to see prices and order</p>
                <p className="text-xs text-[#6f6a63] mb-4">Prices and purchasing are available exclusively for registered customers.</p>
                <div className="flex gap-3 justify-center">
                  <Link to="/login" className="bg-[#2f3e36] text-white px-6 py-2.5 rounded-lg text-xs tracking-wider uppercase font-medium hover:bg-[#253029] transition-colors">
                    Sign In
                  </Link>
                  <Link to="/register" className="border border-[#e7e3dd] px-6 py-2.5 rounded-lg text-xs tracking-wider uppercase font-medium hover:bg-[#fbfaf8] transition-colors">
                    Register
                  </Link>
                </div>
              </div>
            )}

            {/* Quantity - only for authenticated users */}
            {user && (
              <div className="mb-4">
                <label className="text-xs tracking-wider uppercase text-[#6f6a63] block mb-2">Quantity</label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-[#e7e3dd] rounded-lg">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2.5 hover:bg-gray-50"><Minus size={16} /></button>
                    <input
                      data-testid="product-quantity-input"
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value) || 1)))}
                      className="w-16 text-center text-sm border-x border-[#e7e3dd] py-2 focus:outline-none"
                    />
                    <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="p-2.5 hover:bg-gray-50"><Plus size={16} /></button>
                  </div>
                  <div className="text-sm">
                    <span className="text-[#6f6a63]">Unit price: </span>
                    <span className="font-semibold">${currentPrice.toFixed(2)}</span>
                    <span className="text-[#6f6a63]"> | Total: </span>
                    <span className="font-semibold text-lg">${lineTotal}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Pack size, color, stem length, origin & attributes */}
            {product.pack_size && <p className="text-sm text-[#6f6a63] mb-2">Pack Size: {product.pack_size}</p>}
            {product.color && <p className="text-sm text-[#6f6a63] mb-2">Color: {product.color}</p>}
            {product.stem_length && <p className="text-sm text-[#6f6a63] mb-2">Stem Length: {product.stem_length}</p>}
            {product.origin && <p className="text-sm text-[#6f6a63] mb-2">Origin: {product.origin}</p>}
            {Object.entries(product.attributes || {}).map(([key, val]) => (
              <p key={key} className="text-sm text-[#6f6a63] mb-1 capitalize">{key.replace('_', ' ')}: {val}</p>
            ))}

            {/* Actions - only for authenticated users */}
            {user && (
              <div className="flex gap-3 mt-6">
                <button
                  data-testid="product-add-to-cart-button"
                  onClick={handleAddToCart}
                  disabled={stockStatus === 'out_of_stock'}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#2d2d2d] text-white py-3.5 rounded-lg text-sm tracking-wider uppercase font-medium hover:bg-[#232323] transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
                >
                  <ShoppingCart size={18} /> Add to Cart
                </button>
                <button onClick={handleWishlist} className="p-3.5 border border-[#e7e3dd] rounded-lg hover:bg-[#e7f1ea] transition-colors">
                  <Heart size={18} />
                </button>
              </div>
            )}

            {/* Description */}
            {(product.description_short || product.description_long) && (
              <div className="mt-8 border-t border-[#e7e3dd] pt-6">
                <h3 className="text-sm font-semibold tracking-wider uppercase mb-3">Description</h3>
                <p className="text-sm text-[#6f6a63] leading-7">{product.description_long || product.description_short}</p>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="text-base font-semibold tracking-wider uppercase mb-6">Related Products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {related.map((p) => (
                <ProductCard key={p._id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Sticky Add to Cart - only for authenticated users */}
      {user && stockStatus !== 'out_of_stock' && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#e7e3dd] px-4 py-3 z-40 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-xs text-[#6f6a63]">{quantity} x ${currentPrice.toFixed(2)}</p>
            <p className="font-semibold">${lineTotal}</p>
          </div>
          <button onClick={handleAddToCart} className="flex items-center gap-2 bg-[#2d2d2d] text-white px-6 py-3 rounded-lg text-sm tracking-wider uppercase font-medium">
            <ShoppingCart size={16} /> Add to Cart
          </button>
        </div>
      )}
    </div>
  );
}
